import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { MaxBrainUtils } from 'app/projects/core/src/lib/utils';
import { EntityState } from 'app/projects/entity/src/lib/models/entity.state';
import {
    CreateCourseRelevantExecutions,
    CreateCourseRelevantExecutionsFailure,
    CreateCourseRelevantExecutionsSuccess,
    DeleteCourseRelevantExecution,
    DeleteCourseRelevantExecutionFailure,
    DeleteCourseRelevantExecutionSuccess,
    DeselectSelectedCourseRelevantExecutions,
    FetchCourseRelevantExecution,
    FetchCourseRelevantExecutionFailure,
    FetchCourseRelevantExecutionSuccess,
    FetchCourseRelevantExecutions,
    FetchCourseRelevantExecutionsFailure,
    FetchCourseRelevantExecutionsSuccess,
    SetCourseRelevantExecution,
    SetCourseRelevantExecutions,
    UnsetCourseRelevantExecution,
    UpdateCourseRelevantExecution,
    UpdateCourseRelevantExecutionFailure,
    UpdateCourseRelevantExecutionSuccess,
} from '../actions';
import { AddCourseRelevantExecutions } from '../actions/add-entities.action';
import { CourseRelevantExecutionApiService } from '../services/course-relevant-execution.api-service';
import { CourseRelevantExecution } from './course-relevant-execution.model';
import { CourseRelevantExecutionStateModel } from './course-relevant-execution.state-model';
import { HttpErrorResponse } from '@angular/common/http';

@State<CourseRelevantExecutionStateModel>({
    name: 'courseRelevantExecution',
    defaults: new CourseRelevantExecutionStateModel(),
})
@Injectable()
export class CourseRelevantExecutionState extends EntityState<CourseRelevantExecution> {
    static sortProperties: string[] = ['name'];

    /** Ignores out-of-order list responses when switching course offerings quickly. */
    private _latestListFetchKey: string | null = null;

    @Selector()
    static getEntity(state: CourseRelevantExecutionStateModel, id: string = state.item): CourseRelevantExecution {
        if (!state.map) {
            return null;
        }

        state.fetchingList;

        return state.map.get(id) || null;
    }

    @Selector()
    static getEntities(state: CourseRelevantExecutionStateModel): CourseRelevantExecution[] {
        if (!state.map) {
            return null;
        }

        const toReturn = Array.from(state.map.values());

        toReturn.sort(MaxBrainUtils.sortCompareFn(CourseRelevantExecutionState.sortProperties));

        return toReturn;
    }

    @Selector()
    static isFetchingList(state: CourseRelevantExecutionStateModel): boolean {
        return state.fetchingList;
    }

    constructor(private _courseRelevantExecutionApiService: CourseRelevantExecutionApiService) {
        super(null, 0);

        this._propstoSearch = CourseRelevantExecution.props_to_search;
    }

    @Action(AddCourseRelevantExecutions)
    addPendingEntities(ctx: StateContext<CourseRelevantExecutionStateModel>, action: AddCourseRelevantExecutions): void {
        this._addEntities(ctx, action);
    }

    @Action(SetCourseRelevantExecution)
    setEntity(ctx: StateContext<CourseRelevantExecutionStateModel>, action: SetCourseRelevantExecution): void {
        this._setEntity(ctx, action, AddCourseRelevantExecutions);
    }

    @Action(SetCourseRelevantExecutions)
    setEntities(ctx: StateContext<CourseRelevantExecutionStateModel>, action: SetCourseRelevantExecutions): void {
        this._setEntities(ctx, action);
    }

    @Action(UnsetCourseRelevantExecution)
    unsetEntity(ctx: StateContext<CourseRelevantExecutionStateModel>): void {
        this._unsetEntity(ctx);
    }

    @Action(UpdateCourseRelevantExecution)
    updateEntity(ctx: StateContext<CourseRelevantExecutionStateModel>, action: UpdateCourseRelevantExecution): void {
        this._courseRelevantExecutionApiService.update(action.payload.programExecutionId, action.payload.courseOfferingId, action.payload.isDefault).subscribe(
            (entity) => {
                ctx.dispatch(new UpdateCourseRelevantExecutionSuccess(entity));
            },
            (response: HttpErrorResponse) => {
                ctx.dispatch(new UpdateCourseRelevantExecutionFailure(response.error));
            }
        );
    }

    @Action(DeleteCourseRelevantExecution)
    deleteEntity(ctx: StateContext<CourseRelevantExecutionStateModel>, action: DeleteCourseRelevantExecution): void {
        this._courseRelevantExecutionApiService.delete(action.payload.programExecutionId, action.payload.courseOfferingId, action.payload.courseExecutionIds).subscribe(
            () => {
                ctx.dispatch([new DeleteCourseRelevantExecutionSuccess(), new DeselectSelectedCourseRelevantExecutions()]);

                this._removeEntitiesFromState(ctx, action.payload.courseExecutionIds);
            },
            (response: HttpErrorResponse) => {
                ctx.dispatch(new DeleteCourseRelevantExecutionFailure());
            }
        );
    }

    @Action(CreateCourseRelevantExecutions)
    createRelevantExecutions(ctx: StateContext<CourseRelevantExecutionStateModel>, action: CreateCourseRelevantExecutions): void {
        this._courseRelevantExecutionApiService
            .createRelevantExecutions(action.payload.programExecutionId, action.payload.courseOfferingId, action.payload.courseExecutionIds)
            .subscribe(
                () => {
                    ctx.dispatch(new CreateCourseRelevantExecutionsSuccess());
                },
                (response: HttpErrorResponse) => {
                    ctx.dispatch(new CreateCourseRelevantExecutionsFailure());
                }
            );
    }

    @Action(FetchCourseRelevantExecution)
    fetchEntity(ctx: StateContext<CourseRelevantExecutionStateModel>, action: FetchCourseRelevantExecution): void {
        this._fetchEntity(ctx, action, FetchCourseRelevantExecutionSuccess, FetchCourseRelevantExecutionFailure, AddCourseRelevantExecutions, SetCourseRelevantExecution);
    }

    @Action(FetchCourseRelevantExecutionSuccess)
    fetchEntitySuccess(ctx: StateContext<CourseRelevantExecutionStateModel>, action: FetchCourseRelevantExecutionSuccess): void {
        this._fetchEntitySuccess(ctx, action);
    }

    @Action(FetchCourseRelevantExecutionFailure)
    fetchEntityFailure(ctx: StateContext<CourseRelevantExecutionStateModel>, action: FetchCourseRelevantExecutionFailure): void {
        this._fetchEntityFailure(ctx, action);
    }

    @Action(FetchCourseRelevantExecutions)
    fetchEntities(ctx: StateContext<CourseRelevantExecutionStateModel>, action: FetchCourseRelevantExecutions): void {
        const { programExecutionId, courseOfferingId, search, context } = action.payload;
        const fetchKey = `${programExecutionId}:${courseOfferingId}:${search || ''}:${context || ''}`;
        this._latestListFetchKey = fetchKey;

        // Always allow a new request for a different offering; shared entity fetch would otherwise
        // early-return while fetchingList is true and leave the previous course's data on screen.
        ctx.patchState({ fetchingList: true });
        this.clearFetchCacheEntry('list');

        this._courseRelevantExecutionApiService.getAll(programExecutionId, courseOfferingId, search, context).subscribe(
            (entities) => {
                if (this._latestListFetchKey !== fetchKey) {
                    return;
                }

                ctx.dispatch([new SetCourseRelevantExecutions(entities), new FetchCourseRelevantExecutionsSuccess()]);
            },
            () => {
                if (this._latestListFetchKey !== fetchKey) {
                    return;
                }

                ctx.dispatch(new FetchCourseRelevantExecutionsFailure());
            }
        );
    }

    @Action(FetchCourseRelevantExecutionsSuccess)
    fetchEntitiesSuccess(ctx: StateContext<CourseRelevantExecutionStateModel>): void {
        this._fetchEntitiesSuccess(ctx);
    }

    @Action(FetchCourseRelevantExecutionsFailure)
    fetchEntitiesFailure(ctx: StateContext<CourseRelevantExecutionStateModel>): void {
        this._fetchEntitiesFailure(ctx);
    }
}
